import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-2xl border border-slate-200 bg-paper px-4 py-3 text-sm text-ink placeholder:text-slate-400 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20";

export default function EnquiryForm() {
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await axios.post(`${API}/enquiries`, form);
      setState("sent");
    } catch (err) {
      setState("error");
      setError(
        err?.response?.data?.detail ||
          "Something went wrong. Please email us at info@jisll.com.",
      );
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", message: "" });
    setState("idle");
  };

  return (
    <div
      data-testid="enquiry-form-card"
      className="rounded-3xl border border-slate-200/80 bg-white p-8 sm:p-10"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">
        Request Consultation
      </p>
      <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-ink sm:text-3xl">
        Tell us what you're building.
      </h3>

      {state === "sent" ? (
        <div data-testid="enquiry-success-panel" className="flex flex-col items-start gap-5 pt-10 pb-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <div>
            <p className="font-heading text-2xl font-bold tracking-tight text-ink">
              Enquiry received.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
              Thank you — we'll get back to you within one business day.
            </p>
          </div>
          <button
            data-testid="enquiry-send-another"
            onClick={reset}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand transition-colors hover:text-ink"
          >
            Send another enquiry
          </button>
        </div>
      ) : (
        <form data-testid="enquiry-form" onSubmit={submit} className="pt-7">
          <div className="grid gap-4 sm:grid-cols-2">
            <input
              required
              data-testid="enquiry-name-input"
              className={field}
              placeholder="Your name *"
              value={form.name}
              onChange={set("name")}
            />
            <input
              required
              type="email"
              data-testid="enquiry-email-input"
              className={field}
              placeholder="Email *"
              value={form.email}
              onChange={set("email")}
            />
          </div>
          <input
            data-testid="enquiry-phone-input"
            className={`${field} mt-4`}
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={set("phone")}
          />
          <textarea
            required
            rows={4}
            data-testid="enquiry-message-input"
            className={`${field} mt-4 resize-none`}
            placeholder="Tell us what you need *"
            value={form.message}
            onChange={set("message")}
          />
          {state === "error" && (
            <p data-testid="enquiry-error" className="mt-4 text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            data-testid="enquiry-submit-button"
            disabled={state === "sending"}
            className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-hover hover:shadow-[0_12px_32px_rgba(0,82,255,0.4)] disabled:opacity-60 sm:w-auto"
          >
            {state === "sending" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send Enquiry
                <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
